import React, {Fragment, useMemo} from 'react';
import {DataTablePage} from '@common/datatable/page/data-table-page';
import {Trans} from '@ui/i18n/trans';
import {DeleteSelectedItemsAction} from '@common/datatable/page/delete-selected-items-action';
import {DataTableEmptyStateMessage} from '@common/datatable/page/data-table-emty-state-message';
import movieNightImage from './movie-night.svg';
import {DataTableAddItemButton} from '@common/datatable/data-table-add-item-button';
import {Link, useOutletContext} from 'react-router-dom';
import {useTitleIndexFilters} from '@app/titles/use-title-index-filters';
import {TitlesDatatableFilters} from '@app/admin/titles/titles-datatable-filters';
import {useSettings} from '@ui/settings/use-settings';
import {Tooltip} from '@ui/tooltip/tooltip';
import {IconButton} from '@ui/buttons/icon-button';
import {PublishIcon} from '@ui/icons/material/Publish';
import {ImportSingleFromTmdbDialog} from '@app/admin/titles/import/import-single-from-tmdb-dialog';
import {Title, TITLE_MODEL} from '@app/titles/models/title';
import {useAuth} from '@common/auth/use-auth';
import {ColumnConfig} from '@common/datatable/column-config';
import {TitlePoster} from '@app/titles/title-poster/title-poster';
import {TitleLink} from '@app/titles/title-link';
import {FormattedDate} from '@ui/i18n/formatted-date';
import {ChipList} from '@ui/forms/input-field/chip-field/chip-list';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {IconLinkPlus, IconListCheck} from '@tabler/icons-react';
import {useLocalStorage} from '@ui/utils/hooks/local-storage';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {useLinkFiltersData} from '@app/liens/use-link-filters';
import {InfoDialogTrigger} from '@ui/overlays/dialog/info-dialog-trigger/info-dialog-trigger';

import {LearnMoreLink} from '@common/admin/settings/form/learn-more-link';

function LiensDatatableColumns() {
  const {hasPermission} = useAuth();
  const LiensDatatable: ColumnConfig<Title>[] = [
    {
      key: 'name',
      allowsSorting: true,
      width: 'flex-3',
      visibleInMode: 'all',
      header: () => <Trans message="Title" />,
      body: title => (
        <div className="flex items-center gap-12">
          <TitlePoster
            title={title}
            srcSize="sm"
            size="w-32"
            showLastLink={false}
            aspect="aspect-square"
          />
          <div className="min-w-0 overflow-hidden">
            <div className="overflow-hidden overflow-ellipsis">
              <TitleLink title={title} target="_blank" />
            </div>
            <div className="overflow-hidden overflow-ellipsis text-xs text-muted">
              {title.is_series ? (
                <Trans message="Series" />
              ) : (
                <Trans message="Movie" />
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'release_date',
      allowsSorting: true,
      header: () => <Trans message="Release date" />,
      body: title => <FormattedDate date={title.release_date} />,
    },
    {
      key: 'liens_by_active_user_count',
      allowsSorting: true,
      header: () => <Trans message="Valid links" />,
      body: title => (
        <ChipList color={'positive'} radius="rounded" size="xs">
          <Chip key={`active_count_${title.id}`} selectable>
            {title.liens_by_active_user_count}
          </Chip>
        </ChipList>
      ),
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'liens_by_pending_user_count',
      allowsSorting: true,
      header: () => <Trans message="Pending links" />,
      body: title => (
        <ChipList color={'chip'} radius="rounded" size="xs">
          <Chip key={`pending_count_${title.id}`} selectable>
            {title.liens_by_pending_user_count}
          </Chip>
        </ChipList>
      ),
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'liens_by_user_morts_count',
      allowsSorting: true,
      header: () => <Trans message="Dead links" />,
      body: title => (
        <ChipList color={'danger'} radius="rounded" size="xs">
          <Chip key={`morts_count_${title.id}`} selectable>
            {title.liens_by_user_morts_count}
          </Chip>
        </ChipList>
      ),
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'updated_at',
      allowsSorting: true,
      width: 'w-124 flex-shrink-0',
      header: () => <Trans message="Last updated" />,
      body: title =>
        title.updated_at ? <FormattedDate date={title.updated_at} /> : '',
    },
    {
      key: 'actions',
      header: () => <Trans message="Actions" />,
      hideHeader: true,
      visibleInMode: 'all',
      align: 'end',
      width: 'w-84 flex-shrink-0',
      body: title => {
        return (
          <Fragment>
            <Link to={`${title.id}`} className="text-muted">
              <Tooltip label={<Trans message="View links" />}>
                <IconButton size="2xs">
                  <IconListCheck />
                </IconButton>
              </Tooltip>
            </Link>

            {hasPermission('liens.create') && (
              <Link to={`${title.id}/new`} className="text-muted">
                <Tooltip label={<Trans message="Add new link" />}>
                  <IconButton size="2xs">
                    <IconLinkPlus />
                  </IconButton>
                </Tooltip>
              </Link>
            )}
          </Fragment>
        );
      },
    },
  ];
  return LiensDatatable;
}

export function LiensDatatablePage() {
  const [dismissValue] = useLocalStorage<{
    timestamp: number;
  } | null>('links-rules-alert-dismissed', null);
  const {hasPermission} = useAuth();
  const {filters, filtersLoading} = useTitleIndexFilters();
  const filtersDefault = useLinkFiltersData();
  const title = useOutletContext<Title>();
  const mergedFilters = useMemo(() => {
    return [...filters, ...TitlesDatatableFilters(filtersDefault)];
  }, [filters]);

  return (
    <div>
      <DataTablePage
        endpoint="liens"
        headerContent={<InfoTrigger />}
        title={<Trans message="Liens" />}
        columns={LiensDatatableColumns()}
        filters={mergedFilters}
        filtersLoading={filtersLoading}
        enableSelection={hasPermission('titles.delete')}
        actions={<Actions />}
        selectedActions={<DeleteSelectedItemsAction />}
        emptyStateMessage={
          <DataTableEmptyStateMessage
            image={movieNightImage}
            title={<Trans message="No titles have been created yet" />}
            filteringTitle={<Trans message="No matching titles" />}
          />
        }
      />
    </div>
  );
}
function InfoTrigger() {
  return (
    <InfoDialogTrigger
      body={
        <Fragment>
          <Trans message="Before adding links, please read the rules below." />
          <LearnMoreLink
            link="https://darkiworld.wiki/help/hc/articles/7/18/30/reglement-uploader"
            className="mt-14"
          />
        </Fragment>
      }
    />
  );
}

function Actions() {
  const {tmdb_is_setup} = useSettings();

  return (
    <Fragment>
      {tmdb_is_setup && <ImportButton />}
      <DataTableAddItemButton elementType={Link} to="/admin/titles/new">
        <Trans message="Add title" />
      </DataTableAddItemButton>
    </Fragment>
  );
}

function ImportButton() {
  return (
    <DialogTrigger type="modal">
      <Tooltip label={<Trans message="Import from external source" />}>
        <IconButton
          variant="outline"
          color="primary"
          className="flex-shrink-0"
          size="sm"
        >
          <PublishIcon />
        </IconButton>
      </Tooltip>
      <ImportSingleFromTmdbDialog modelType={TITLE_MODEL} />
    </DialogTrigger>
  );
}
